import { Component, ElementRef, QueryList, ViewChildren } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import {
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  AbstractControl,
  ValidationErrors,
  ValidatorFn
} from '@angular/forms';
import Swal from 'sweetalert2';
import { PatientService } from '@core/http/patient.service';
import { HttpMesageHandlerService } from '@core/service/http-mesage-handler';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';

export const nipPacienteValidator = (length: number): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const raw = (control.value ?? '').toString().trim();
    if (!raw) return { required: true };

    if (raw.length !== length || !/^\d+$/.test(raw)) return { nipFormat: true };

    const distinct = new Set(raw.split(''));
    if (distinct.size !== raw.length) return { nipRepeated: true };

    // bloquea cualquier consecutivo de 2 o más
    for (let i = 0; i < raw.length - 1; i++) {
      const a = raw.charCodeAt(i) - 48;
      const b = raw.charCodeAt(i + 1) - 48;

      const asc = b === a + 1;
      const desc = b === a - 1;

      if (asc || desc) return { nipSequence: true };
    }

    return null;
  };
};

export const pin6DigitsValidator: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
  const raw = (control.value ?? '').toString().trim();
  if (!raw) return { required: true };
  if (!/^\d{6}$/.test(raw)) return { pinFormat: true };
  return null;
};

export const matchPinsValidator: ValidatorFn = (group: AbstractControl): ValidationErrors | null => {
  const g: any = group;
  const newPin = (g.get?.('newPin')?.value ?? '').toString().trim();
  const confirm = (g.get?.('confirmNewPin')?.value ?? '').toString().trim();
  if (confirm.length < 6) return null;
  return newPin === confirm ? null : { pinMismatch: true };
};

@Component({
  selector: 'app-recuperar-pin',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatButtonModule],
  templateUrl: './recuperar-pin.component.html',
  styleUrl: './recuperar-pin.component.scss',
})
export class RecuperarPinComponent {
  token = '';
  form: UntypedFormGroup;
  saving = false;

  pinDigits = Array.from({ length: 6 });
  newPinOtp: string[] = ['', '', '', '', '', ''];
  confirmOtp: string[] = ['', '', '', '', '', ''];

  showPinErrorModal = false;
  pinErrorModalTitle = 'REVISA EL PIN';
  pinErrorModalMessage = '';

  @ViewChildren('newPinDigit') newPinDigitInputs!: QueryList<ElementRef<HTMLInputElement>>;
  @ViewChildren('confirmPinDigit') confirmPinDigitInputs!: QueryList<ElementRef<HTMLInputElement>>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: UntypedFormBuilder,
    private patientService: PatientService,
    private httpMsgHandler: HttpMesageHandlerService,
  ) {
    this.form = this.fb.group(
      {
        newPin: ['', [nipPacienteValidator(6)]],
        confirmNewPin: ['', [pin6DigitsValidator]],
      },
      { validators: [matchPinsValidator] }
    );

    this.route.queryParamMap.subscribe(q => {
      const raw = q.get('token') ?? '';
      const normalized = raw.replace(/ /g, '+');
      this.token = decodeURIComponent(normalized).trim();

      if (!this.token) {
        Swal.fire({
          icon: 'error',
          title: 'ENLACE INVÁLIDO',
          text: 'EL ENLACE NO CONTIENE TOKEN.',
          confirmButtonText: 'ACEPTAR'
        }).then(() => this.router.navigate(['/authentication/signin']));
        return;
      }

      if (!this.token.includes(':')) {
        Swal.fire({
          icon: 'error',
          title: 'TOKEN INVÁLIDO',
          text: 'EL TOKEN NO TIENE EL FORMATO ESPERADO.',
          confirmButtonText: 'ACEPTAR'
        }).then(() => this.router.navigate(['/authentication/signin']));
        return;
      }

      setTimeout(() => this.focusOtp('new', 0), 0);
    });
  }

  get newPinControl() {
    return this.form.get('newPin');
  }

  get confirmPinControl() {
    return this.form.get('confirmNewPin');
  }

  get newPinLiveMessage(): string {
    const newPin = this.newPinOtp.join('');
    const confirmPin = this.confirmOtp.join('');

    if (!newPin && !confirmPin) {
      return 'SOLO NÚMEROS. NO REPITAS DÍGITOS Y EVITA CONSECUTIVOS (EJ: 12, 21, 45, 54). EJ: 507194';
    }

    if (newPin.length < 6) {
      return `TE FALTAN ${6 - newPin.length} DÍGITO(S) EN EL PIN NUEVO.`;
    }

    const newPinError = this.getNewPinValidationMessage();
    if (newPinError) {
      return newPinError;
    }

    if (!confirmPin) {
      return 'AHORA CONFIRMA TU PIN NUEVO.';
    }

    if (confirmPin.length < 6) {
      return `TE FALTAN ${6 - confirmPin.length} DÍGITO(S) EN LA CONFIRMACIÓN.`;
    }

    if (this.form.hasError('pinMismatch')) {
      return 'EL PIN NUEVO Y SU CONFIRMACIÓN NO COINCIDEN.';
    }

    return 'PIN VÁLIDO ✅';
  }

  get isNewPinLiveSuccess(): boolean {
    return this.newPinLiveMessage === 'PIN VÁLIDO ✅';
  }

  private focusOtp(type: 'new' | 'confirm', i: number) {
    const list = type === 'new'
      ? (this.newPinDigitInputs?.toArray?.() ?? [])
      : (this.confirmPinDigitInputs?.toArray?.() ?? []);
    const el = list[i]?.nativeElement;
    if (el) {
      el.focus();
      el.select();
    }
  }

  private sync(type: 'new' | 'confirm', markTouched = false) {
    const v = (type === 'new' ? this.newPinOtp : this.confirmOtp).join('');
    const ctrlName = type === 'new' ? 'newPin' : 'confirmNewPin';

    this.form.patchValue({ [ctrlName]: v }, { emitEvent: false });

    const ctrl = this.form.get(ctrlName);
    if (markTouched) ctrl?.markAsTouched();
    ctrl?.updateValueAndValidity({ emitEvent: false });
    this.form.updateValueAndValidity({ emitEvent: false });
  }

  onOtpInput(event: Event, index: number, type: 'new' | 'confirm') {
    const input = event.target as HTMLInputElement;
    let v = (input.value ?? '').replace(/\D/g, '');
    if (v.length > 1) v = v.slice(-1);

    const arr = [...(type === 'new' ? this.newPinOtp : this.confirmOtp)];
    arr[index] = v;

    if (type === 'new') this.newPinOtp = arr;
    else this.confirmOtp = arr;

    this.sync(type, false);

    if (v && index < 5) this.focusOtp(type, index + 1);
    if (!arr.includes('')) this.sync(type, true);
  }

  onOtpKeyDown(event: KeyboardEvent, index: number, type: 'new' | 'confirm') {
    const key = event.key;
    const allowed =
      /^[0-9]$/.test(key) ||
      key === 'Backspace' ||
      key === 'Delete' ||
      key === 'ArrowLeft' ||
      key === 'ArrowRight' ||
      key === 'Tab';

    if (!allowed) {
      event.preventDefault();
      return;
    }

    if (key === 'ArrowLeft') {
      event.preventDefault();
      this.focusOtp(type, Math.max(0, index - 1));
      return;
    }

    if (key === 'ArrowRight') {
      event.preventDefault();
      this.focusOtp(type, Math.min(5, index + 1));
      return;
    }

    if (key === 'Backspace') {
      const arr = [...(type === 'new' ? this.newPinOtp : this.confirmOtp)];
      const current = arr[index];

      if (!current && index > 0) {
        event.preventDefault();
        arr[index - 1] = '';
        if (type === 'new') this.newPinOtp = arr;
        else this.confirmOtp = arr;
        this.sync(type, false);
        this.focusOtp(type, index - 1);
      } else {
        setTimeout(() => {
          const list = type === 'new'
            ? (this.newPinDigitInputs?.toArray?.() ?? [])
            : (this.confirmPinDigitInputs?.toArray?.() ?? []);
          const el = list[index]?.nativeElement;
          const raw = (el?.value ?? '').replace(/\D/g, '');
          arr[index] = raw ? raw.slice(-1) : '';
          if (type === 'new') this.newPinOtp = arr;
          else this.confirmOtp = arr;
          this.sync(type, false);
        }, 0);
      }
      return;
    }

    if (/^[0-9]$/.test(key)) {
      event.preventDefault();
      const arr = [...(type === 'new' ? this.newPinOtp : this.confirmOtp)];
      arr[index] = key;
      if (type === 'new') this.newPinOtp = arr;
      else this.confirmOtp = arr;
      this.sync(type, false);

      if (index < 5) this.focusOtp(type, index + 1);
      else this.sync(type, true);
    }
  }

  onOtpPaste(event: ClipboardEvent, type: 'new' | 'confirm') {
    event.preventDefault();
    const text = (event.clipboardData?.getData('text') ?? '').replace(/\D/g, '').slice(0, 6);
    if (!text) return;

    const arr = ['', '', '', '', '', ''];
    for (let i = 0; i < 6; i++) arr[i] = text[i] ?? '';

    if (type === 'new') this.newPinOtp = arr;
    else this.confirmOtp = arr;

    this.sync(type, true);

    const nextEmpty = arr.findIndex(x => !x);
    setTimeout(() => this.focusOtp(type, nextEmpty === -1 ? 5 : nextEmpty), 0);
  }

  private getNewPinValidationMessage(): string {
    const ctrl = this.newPinControl;
    if (!ctrl) return '';

    if (ctrl.hasError('required')) return 'CAPTURA EL PIN NUEVO.';
    if (ctrl.hasError('nipFormat') || ctrl.hasError('pinFormat')) return 'DEBE SER DE 6 DÍGITOS NUMÉRICOS.';
    if (ctrl.hasError('nipRepeated')) return 'NO REPITAS DÍGITOS EN EL PIN.';
    if (ctrl.hasError('nipSequence')) return 'NO SE PERMITEN NÚMEROS CONSECUTIVOS (EJ: 12, 21, 45, 54).';

    return '';
  }

  private getConfirmValidationMessage(): string {
    const ctrl = this.confirmPinControl;
    if (!ctrl) return '';

    if (ctrl.hasError('required')) return 'CONFIRMA EL PIN NUEVO.';
    if (ctrl.hasError('pinFormat')) return 'LA CONFIRMACIÓN DEBE SER DE 6 DÍGITOS NUMÉRICOS.';
    if (this.form.hasError('pinMismatch')) return 'EL PIN NUEVO Y SU CONFIRMACIÓN NO COINCIDEN.';

    return '';
  }

  private getSubmitErrorMessage(): string {
    const newPinError = this.getNewPinValidationMessage();
    if (newPinError) return newPinError;

    const confirmError = this.getConfirmValidationMessage();
    if (confirmError) return confirmError;

    return 'REVISA EL PIN CAPTURADO.';
  }

  openPinErrorModal(message: string, title = 'REVISA EL PIN'): void {
    this.pinErrorModalTitle = title;
    this.pinErrorModalMessage = message;
    this.showPinErrorModal = true;
  }

  closePinErrorModal(): void {
    this.showPinErrorModal = false;
  }

  submit(): void {
    this.form.markAllAsTouched();
    this.form.updateValueAndValidity({ emitEvent: false });

    if (this.form.invalid) {
      this.openPinErrorModal(this.getSubmitErrorMessage());
      return;
    }

    const pin = String(this.form.get('newPin')?.value ?? '').trim();

    this.saving = true;

    this.patientService.setPinPacienteWithBearerToken({ pin }, this.token).subscribe({
      next: (res: any) => {
        this.httpMsgHandler.handleMsg(res);

        Swal.fire({
          icon: 'success',
          title: 'PIN ACTUALIZADO',
          text: 'YA PUEDES REGRESAR A LA APP E INICIAR SESIÓN.',
          confirmButtonText: 'ACEPTAR'
        }).then(() => this.router.navigate(['/']));
      },
      error: (err: HttpErrorResponse) => {
        this.httpMsgHandler.handleMsg(err);
        this.saving = false;
      },
      complete: () => (this.saving = false)
    });
  }
}
